/**
 * Escapes a single cell value per RFC 4180:
 * - Wrap in double quotes if the value contains a comma, double quote, or newline.
 * - Escape existing double quotes by doubling them.
 */
function escapeCell(value: string): string {
  if (value.includes('"') || value.includes(',') || value.includes('\n') || value.includes('\r')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export interface CsvColumn<T> {
  header: string;
  value: (row: T) => string | number | boolean | null | undefined;
}

/**
 * Converts an array of rows to a CSV string using RFC 4180 conventions.
 * - CRLF line endings.
 * - null/undefined values render as empty cells.
 */
export function toCsv<T>(rows: T[], columns: CsvColumn<T>[]): string {
  const lines: string[] = [];

  // Header row
  lines.push(columns.map((col) => escapeCell(col.header)).join(','));

  // Data rows
  for (const row of rows) {
    const cells = columns.map((col) => {
      const raw = col.value(row);
      if (raw === null || raw === undefined) return '';
      return escapeCell(String(raw));
    });
    lines.push(cells.join(','));
  }

  return lines.join('\r\n');
}

/**
 * Triggers a browser download of the given CSV content as a file.
 */
export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
